import areaPulse from './weapons/area_pulse.json';
import boomerangBlade from './weapons/boomerang_blade.json';
import basicShot from './weapons/basic_shot.json';
import chainArc from './weapons/chain_arc.json';
import deathSpiral from './weapons/death_spiral.json';
import holyWand from './weapons/holy_wand.json';
import orbitShield from './weapons/orbit_shield.json';
import prismBeam from './weapons/prism_beam.json';
import armor from './passives/armor.json';
import haste from './passives/haste.json';
import magnet from './passives/magnet.json';
import might from './passives/might.json';
import speed from './passives/speed.json';
import basicShotHolyWand from './evolutions/basic_shot_holy_wand.json';
import orbitShieldDeathSpiral from './evolutions/orbit_shield_death_spiral.json';
import gold25 from './consumables/gold_25.json';
import heal20 from './consumables/heal_20.json';
import boss from './enemies/boss.json';
import basicEnemy from './enemies/basic_enemy.json';
import dashStalker from './enemies/dash_stalker.json';
import fastEnemy from './enemies/fast_enemy.json';
import ironColossus from './enemies/iron_colossus.json';
import rangedAcolyte from './enemies/ranged_acolyte.json';
import tankEnemy from './enemies/tank_enemy.json';
import voidTyrant from './enemies/void_tyrant.json';
import gameSettings from './game/settings.json';
import waveTable from './waves/wave_table.json';
import biomes from './biomes.json';
import { createLogger, Logger } from '../utils/Logger';

export const CONTENT_SOURCE = 'bundled-json-imports' as const;

export type ContentType =
  | 'weapon'
  | 'passive'
  | 'evolution'
  | 'consumable'
  | 'enemy'
  | 'biome'
  | 'game'
  | 'wave';

export interface ContentManifestEntry {
  readonly type: ContentType;
  readonly id: string;
  readonly schemaId: string;
  readonly path: string;
  readonly required: boolean;
  readonly data: unknown;
}

export const CONTENT_MANIFEST: readonly ContentManifestEntry[] = [
  { type: 'weapon', id: 'basic_shot', schemaId: 'weapon', path: 'src/data/weapons/basic_shot.json', required: true, data: basicShot },
  { type: 'weapon', id: 'orbit_shield', schemaId: 'weapon', path: 'src/data/weapons/orbit_shield.json', required: true, data: orbitShield },
  { type: 'weapon', id: 'holy_wand', schemaId: 'weapon', path: 'src/data/weapons/holy_wand.json', required: true, data: holyWand },
  { type: 'weapon', id: 'death_spiral', schemaId: 'weapon', path: 'src/data/weapons/death_spiral.json', required: true, data: deathSpiral },
  { type: 'weapon', id: 'area_pulse', schemaId: 'weapon', path: 'src/data/weapons/area_pulse.json', required: true, data: areaPulse },
  { type: 'weapon', id: 'prism_beam', schemaId: 'weapon', path: 'src/data/weapons/prism_beam.json', required: true, data: prismBeam },
  { type: 'weapon', id: 'boomerang_blade', schemaId: 'weapon', path: 'src/data/weapons/boomerang_blade.json', required: true, data: boomerangBlade },
  { type: 'weapon', id: 'chain_arc', schemaId: 'weapon', path: 'src/data/weapons/chain_arc.json', required: true, data: chainArc },
  { type: 'passive', id: 'might', schemaId: 'passive', path: 'src/data/passives/might.json', required: true, data: might },
  { type: 'passive', id: 'haste', schemaId: 'passive', path: 'src/data/passives/haste.json', required: true, data: haste },
  { type: 'passive', id: 'armor', schemaId: 'passive', path: 'src/data/passives/armor.json', required: true, data: armor },
  { type: 'passive', id: 'speed', schemaId: 'passive', path: 'src/data/passives/speed.json', required: true, data: speed },
  { type: 'passive', id: 'magnet', schemaId: 'passive', path: 'src/data/passives/magnet.json', required: true, data: magnet },
  { type: 'evolution', id: 'basic_shot_holy_wand', schemaId: 'evolution', path: 'src/data/evolutions/basic_shot_holy_wand.json', required: true, data: basicShotHolyWand },
  { type: 'evolution', id: 'orbit_shield_death_spiral', schemaId: 'evolution', path: 'src/data/evolutions/orbit_shield_death_spiral.json', required: true, data: orbitShieldDeathSpiral },
  { type: 'consumable', id: 'heal_20', schemaId: 'consumable', path: 'src/data/consumables/heal_20.json', required: true, data: heal20 },
  { type: 'consumable', id: 'gold_25', schemaId: 'consumable', path: 'src/data/consumables/gold_25.json', required: true, data: gold25 },
  { type: 'enemy', id: 'basic_enemy', schemaId: 'enemy', path: 'src/data/enemies/basic_enemy.json', required: true, data: basicEnemy },
  { type: 'enemy', id: 'fast_enemy', schemaId: 'enemy', path: 'src/data/enemies/fast_enemy.json', required: true, data: fastEnemy },
  { type: 'enemy', id: 'tank_enemy', schemaId: 'enemy', path: 'src/data/enemies/tank_enemy.json', required: true, data: tankEnemy },
  { type: 'enemy', id: 'dash_stalker', schemaId: 'enemy', path: 'src/data/enemies/dash_stalker.json', required: true, data: dashStalker },
  { type: 'enemy', id: 'ranged_acolyte', schemaId: 'enemy', path: 'src/data/enemies/ranged_acolyte.json', required: true, data: rangedAcolyte },
  { type: 'enemy', id: 'boss', schemaId: 'enemy', path: 'src/data/enemies/boss.json', required: true, data: boss },
  { type: 'enemy', id: 'void_tyrant', schemaId: 'enemy', path: 'src/data/enemies/void_tyrant.json', required: true, data: voidTyrant },
  { type: 'enemy', id: 'iron_colossus', schemaId: 'enemy', path: 'src/data/enemies/iron_colossus.json', required: true, data: ironColossus },
  ...biomes.map(biome => ({
    type: 'biome' as const,
    id: biome.id,
    schemaId: 'biome',
    path: `src/data/biomes.json#${biome.id}`,
    required: true,
    data: biome
  })),
  { type: 'game', id: 'settings', schemaId: 'game', path: 'src/data/game/settings.json', required: true, data: gameSettings },
  { type: 'wave', id: 'wave_table', schemaId: 'wave', path: 'src/data/waves/wave_table.json', required: true, data: waveTable }
];

export function getContentManifest(logger: Logger = createLogger('ContentManifest')): readonly ContentManifestEntry[] {
  logger.debug('getContentManifest:start', {
    source: CONTENT_SOURCE,
    entries: CONTENT_MANIFEST.map(entry => ({
      type: entry.type,
      id: entry.id,
      schemaId: entry.schemaId,
      path: entry.path,
      required: entry.required
    }))
  });

  return CONTENT_MANIFEST;
}

export function assertContentManifest(logger: Logger = createLogger('ContentManifest')): void {
  logger.debug('assertContentManifest:start', { source: CONTENT_SOURCE });

  const missingRequiredEntries = CONTENT_MANIFEST.filter(entry => entry.required && entry.data === undefined);
  if (missingRequiredEntries.length > 0) {
    logger.error('assertContentManifest:missing-required-entries', {
      missingRequiredEntries: missingRequiredEntries.map(entry => ({
        type: entry.type,
        id: entry.id,
        path: entry.path
      }))
    });
    throw new Error('Missing required content manifest entries');
  }

  logger.debug('assertContentManifest:complete', {
    source: CONTENT_SOURCE,
    requiredEntries: CONTENT_MANIFEST.filter(entry => entry.required).length
  });
}
